import { effect, inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LoginFormComponent } from '../components/molecules/login-form/login-form.component';
import { getState } from '@ngrx/signals';
import { ActiveUserStore } from '../stores/user/active-user.state';
import { ConfirmationDialogService } from '../services/confirmation-dialog.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  private loginDialogRef: DynamicDialogRef | null = null; // Keep track of open dialog
  private lastErrorTime = 0;
  private readonly errorDebounceMs: number = 300; // Debounce 401/403 errors by 30 millisecond
  readonly userStore = inject(ActiveUserStore);
  private readonly confirmationDialogService = inject(
    ConfirmationDialogService
  );

  constructor(private dialogService: DialogService) {
    effect(() => {
      const userState = getState(this.userStore);
      if (userState.isLoggedIn && this.loginDialogRef) {
        this.loginDialogRef.close();
        // Without this, `loginDialogRef` stays non-null after the first
        // re-login, so `!this.loginDialogRef` in intercept() is never true
        // again and a second session expiry never reopens the dialog.
        this.loginDialogRef = null;
      }
    });
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for auth status checks, 2FA-related requests and guest
    // links (a wrong guest password answers 401 - that's no expired login;
    // the password dialog shows the error itself)
    if (
      req.url.includes('api/authentication/status') ||
      req.url.includes('zwei-faktor') ||
      req.url.includes('api/bookmark/token/')
    ) {
      return next.handle(req); // Pass the request through without changes
    }

    // Public atlas / guest links are for end users without a commune login:
    // a 401/403 there is never something they can act on (no login dialog,
    // no "Keine Berechtigung" toast - the caller just gets no data). Third
    // party APIs (Wegweiser proxy, Mapbox) answer with their own statuses
    // that say nothing about the Hyperion session either.
    const context = this.userStore.context();
    if (
      context === 'atlasExternal' ||
      context === 'guestView' ||
      !req.url.startsWith(`${environment.apiUrl}/api/`)
    ) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Handle 401/403 Unauthorized error
        const is2FARoute = window.location.pathname.includes(
          'zwei-faktor-authentifizierung'
        );
        const now = Date.now();
        const shouldHandleError =
          now - this.lastErrorTime > this.errorDebounceMs;

        // 403 = logged in but not allowed: explain, don't ask to log in
        // again (a re-login - possibly as someone else - over the current
        // state would not help and could mix up two users' data)
        if (error.status === 403 && !is2FARoute && shouldHandleError) {
          this.lastErrorTime = now;
          this.confirmationDialogService.addErrorMessage(
            'Für diese Aktion fehlt Ihnen die Berechtigung.',
            'Keine Berechtigung'
          );
        }

        if (error.status === 401 && !is2FARoute && shouldHandleError) {
          console.error('API request failed with 401: Unauthorized');
          this.lastErrorTime = now;

          // Open the login dialog if not already open
          if (!this.loginDialogRef) {
            this.loginDialogRef = this.dialogService.open(LoginFormComponent, {
              header: 'Ihr Login ist abgelaufen',
              width: '33rem',
              closable: false,
            });
          }
        }

        // Pass the error to the caller
        return throwError(() => error);
      })
    );
  }
}
